'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Check, Link2, Loader2, MessagesSquare, Share2, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import { shareOrCopy, type ShareOutcome } from '@/lib/share';
import { createShare } from '@/lib/api';
import { shareCreateMessage } from '@/lib/sharedHome';
import { useAuthStore } from '@/stores/authStore';

interface ShareButtonProps {
  title: string;
  /** One line of real listing detail to ride along with the link. */
  text?: string;
  /**
   * The home this button is on. Present → a signed-in buyer is additionally
   * offered a link that can be replied to. Absent → the button behaves exactly
   * as it always has.
   */
  listingId?: string;
  className?: string;
}

/**
 * Native share sheet where available, clipboard fallback elsewhere — and an
 * honest answer either way.
 *
 * Two silent failures this replaces (W13 R124): dismissing the native sheet
 * rejects with `AbortError`, and the old `catch` fell straight through to the
 * clipboard and flashed **"Link copied!"** — doing something after the buyer
 * said no. At the other end, a clipboard rejection was swallowed with "nothing
 * sensible left to do", so on a browser without clipboard permission the button
 * did *nothing*, with no feedback of any kind.
 *
 * **A signed-in buyer gets a second kind of link.** `POST /api/v1/shares` mints a
 * `/shared/{token}` page where whoever receives it can react and comment without
 * an account, and the buyer hears back. That is a different object from the
 * public listing URL — it can be revoked, and it carries a conversation — so it
 * is offered as a choice rather than swapped in: one tap still copies the plain
 * link, which is what somebody sending a home to themselves wants.
 *
 * **Signed out, nothing changed.** No popover, no request, no account prompt —
 * the same single-tap copy this button has always been. Sharing a house is not a
 * moment to interrupt with a sign-up.
 */
export default function ShareButton({ title, text, listingId, className }: ShareButtonProps) {
  const [outcome, setOutcome] = useState<ShareOutcome | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { user } = useAuthStore();
  // Server-rendered and first-client-rendered as `false` (the store starts
  // signed-out and resolves in an effect), so this cannot mismatch on hydration
  // — it upgrades a moment later for somebody with a session.
  const canInvite = Boolean(listingId) && Boolean(user);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const flash = useCallback((result: ShareOutcome) => {
    // A dismissal needs no feedback — the buyer knows what they just did.
    if (result === 'dismissed' || result === 'shared') return;
    setOutcome(result);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setOutcome(null), result === 'failed' ? 4000 : 1800);
  }, []);

  const handleShare = useCallback(async () => {
    // Always the live URL, so sharing from an open gallery shares the photo the
    // buyer is actually looking at (`?photo=N`, W13 R123).
    const result = await shareOrCopy({ title, text, url: window.location.href });
    flash(result);
  }, [title, text, flash]);

  const failed = outcome === 'failed';

  const trigger = (
    <button
      onClick={canInvite ? undefined : handleShare}
      className={cn(
        'glass flex h-12 w-12 items-center justify-center rounded-full transition-all hover:bg-glass-bright',
        className
      )}
      aria-label="Share this listing"
      title={
        failed
          ? "Couldn't copy — the link is in your address bar"
          : outcome === 'copied'
            ? 'Link copied!'
            : 'Share'
      }
    >
      {/* The result of the buyer's own tap, announced. The old button said
          nothing to a screen reader either way. */}
      <span className="sr-only" role="status" aria-live="polite">
        {failed
          ? "Couldn't copy the link — copy it from the address bar instead."
          : outcome === 'copied'
            ? 'Link copied to clipboard'
            : ''}
      </span>
      {failed ? (
        <TriangleAlert className="h-5 w-5 text-amber-500" />
      ) : outcome === 'copied' ? (
        <Check className="h-5 w-5 text-emerald-400" />
      ) : (
        <Share2 className="h-5 w-5 text-ink-soft" />
      )}
    </button>
  );

  if (!canInvite) return trigger;

  return (
    <Popover.Root>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          sideOffset={8}
          align="end"
          collisionPadding={12}
          className="glass-blur z-[100] w-[19rem] max-w-[calc(100vw-1.5rem)] rounded-xl border border-hairline-strong bg-raised/95 p-2 shadow-lift-lg"
        >
          <button
            type="button"
            onClick={handleShare}
            className="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-glass"
          >
            <Link2 aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" />
            <span>
              <span className="block text-sm font-medium text-ink">Copy this link</span>
              <span className="block text-xs text-ink-muted">
                The listing page, exactly as you see it.
              </span>
            </span>
          </button>

          <InviteAction listingId={listingId!} title={title} onOutcome={flash} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

type InviteState =
  | { kind: 'idle' }
  | { kind: 'creating' }
  | { kind: 'ready'; url: string; outcome: ShareOutcome | null }
  | { kind: 'failed'; message: string };

/**
 * "Share with a friend" — mint a `/shared/{token}` page and hand over the link.
 *
 * The created URL is **shown as well as shared**. Every step after minting can
 * fail in a way the buyer cannot see: the native sheet can be dismissed, the
 * clipboard can be refused outright by permissions. A link that exists on the
 * server but nowhere the buyer can reach is the worst outcome available here, so
 * once it exists it stays on screen, selectable, until the popover closes.
 */
function InviteAction({
  listingId,
  title,
  onOutcome,
}: {
  listingId: string;
  title: string;
  onOutcome: (outcome: ShareOutcome) => void;
}) {
  const [state, setState] = useState<InviteState>({ kind: 'idle' });

  async function offer(url: string) {
    const result = await shareOrCopy({
      title,
      text: `Take a look and tell me what you think — you can react and leave notes, no account needed.`,
      url,
    });
    setState({ kind: 'ready', url, outcome: result });
    onOutcome(result);
  }

  async function invite() {
    if (state.kind === 'creating') return;
    // Already minted in this session — re-offer the same link rather than
    // asking the server for another one.
    if (state.kind === 'ready') {
      await offer(state.url);
      return;
    }
    setState({ kind: 'creating' });
    try {
      const share = await createShare(listingId);
      await offer(share.url);
    } catch (err) {
      setState({ kind: 'failed', message: shareCreateMessage(err) });
    }
  }

  return (
    <div className="mt-1 border-t border-hairline pt-1">
      <button
        type="button"
        onClick={invite}
        disabled={state.kind === 'creating'}
        className="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-glass disabled:opacity-60"
      >
        {state.kind === 'creating' ? (
          <Loader2 aria-hidden className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-emerald-400" />
        ) : (
          <MessagesSquare aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
        )}
        <span>
          <span className="block text-sm font-medium text-ink">Share with a friend</span>
          <span className="block text-xs text-ink-muted">
            They can react and leave notes — no account needed — and you&rsquo;ll see what they
            said.
          </span>
        </span>
      </button>

      {state.kind === 'ready' && (
        <div className="px-3 pb-2">
          <label htmlFor="share-invite-url" className="sr-only">
            Your share link
          </label>
          <input
            id="share-invite-url"
            readOnly
            value={state.url}
            onFocus={(e) => e.currentTarget.select()}
            className="w-full rounded-lg border border-hairline bg-glass px-2 py-1.5 text-xs text-ink-soft"
          />
          <p role="status" className="mt-1.5 text-xs text-ink-muted">
            {state.outcome === 'copied'
              ? 'Copied — paste it wherever you like.'
              : state.outcome === 'failed'
                ? "We couldn't copy it for you, so here it is to copy by hand."
                : 'Your link is ready.'}
          </p>
        </div>
      )}

      {state.kind === 'failed' && (
        <p role="alert" className="px-3 pb-2 text-xs text-coral-300">
          {state.message}
        </p>
      )}
    </div>
  );
}
